import { FC, useCallback, useMemo, useState } from 'react';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import SidePanel from 'src/components/design-system/SidePanel';
import useBoolean from 'src/hooks/useBoolean';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useBulkUpdateGLCodeMutation from 'src/services/department/useBulkUpdateGLCodeMutation';
import { GlCodeResponse } from 'src/types/GLCode';
import { Department } from 'src/types/department';
import { findCommonGLCodes, mergeGLCodesArrays } from 'src/utils/transformation';
import DepartmentsTabs from '../DepartmentsTabs';
import { initialDepartmentState } from '../ViewDepartmentContext';
import AssignGLCodeTable from './AssignGLCodeTable';
import MultiSelectDepartment from './MultiSelectDepartment';
import UnassignGLCodeTable from './UnassignGLCodeTable';

interface BulkUpdateGLCodeProps {}

const Tabs = ['Assign', 'Unassign'];
const BulkUpdateGLCode: FC<BulkUpdateGLCodeProps> = () => {
  const {
    t,
    unDeletedDepartmentList,
    glCodes,
    selectedRows,
    setSelectedRows,
    setDepartmentsDataState,
    selectedCompany,
  } = useViewDepartment();
  const [assignGLCode, setAssignGLCode] = useState<GlCodeResponse[]>([]);
  const [unAssignGLCode, setUnAssignGLCode] = useState<GlCodeResponse[]>([]);

  const { mutateAsync: bulkUpdateGLCode, isPending: isBulkUpdateGLCodeLoading } =
    useBulkUpdateGLCodeMutation({
      companyId: selectedCompany.companyId,
    });

  const { isBulkUpdateGLCodeDisable, message } = useMemo(() => {
    const isBulkUpdateGLCodeDisable = selectedRows.length < 1;
    let message = '';
    if (isBulkUpdateGLCodeDisable) {
      message = 'Please select Department(s) to assign GL Codes';
    }
    return {
      isBulkUpdateGLCodeDisable,
      message,
    };
  }, [selectedRows.length]);

  const { value: isPaneOpen, setTrue: openPane, setFalse: closePane } = useBoolean(false);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const handleSelectTab = useCallback((num: number) => {
    setSelectedTab(num);
  }, []);

  const handlePaneClose = useCallback(() => {
    if (!isBulkUpdateGLCodeLoading) {
      closePane();
      setSelectedTab(0);
    }
  }, [closePane, isBulkUpdateGLCodeLoading]);

  const setInitialGLCodes = useCallback(
    (selectedRows: Department[]) => {
      const mergeGLCodes = mergeGLCodesArrays(selectedRows);
      const commonGLCodes = findCommonGLCodes(selectedRows);

      const initialUnassignGLCode = glCodes
        .filter((glCode) => mergeGLCodes.some((g) => g.id === glCode.id))
        .sort((a, b) => a.name.localeCompare(b.name));
      const initialAssignGLCode = glCodes
        .filter((glCode) => !commonGLCodes.some((g) => g.id === glCode.id))
        .sort((a, b) => a.name.localeCompare(b.name));
      setAssignGLCode(initialAssignGLCode.map((glCode) => ({ ...glCode, selected: false })));
      setUnAssignGLCode(initialUnassignGLCode.map((glCode) => ({ ...glCode, selected: false })));
    },
    [glCodes]
  );
  const handleGLCodeUpdateClick = useCallback(() => {
    openPane();
    setInitialGLCodes(selectedRows);
    setDepartmentsDataState(initialDepartmentState);
  }, [openPane, selectedRows, setDepartmentsDataState, setInitialGLCodes]);

  const handleDepartments = useCallback(
    (departments: Department[]) => {
      setSelectedRows(departments);
      setInitialGLCodes(departments);
    },
    [setInitialGLCodes, setSelectedRows]
  );
  return (
    <>
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper type={'Information'} contents={[message]} />
          </div>
        }
        show={isBulkUpdateGLCodeDisable}
      >
        <Button
          color="primary"
          onClick={handleGLCodeUpdateClick}
          disabled={isBulkUpdateGLCodeDisable}
        >
          {t('Bulk GL Code Update')}
        </Button>
      </PopUp>

      <SidePanel
        isOpen={isPaneOpen}
        onClose={handlePaneClose}
        position="right"
        title="Bulk Update GL Codes"
        className="bg-[#EEEEEE]"
        width="750px"
      >
        <MultiSelectDepartment
          departmentList={unDeletedDepartmentList}
          selectedDepartments={selectedRows}
          setSelectedDepartments={handleDepartments}
        />
        {Tabs[selectedTab] === 'Assign' ? (
          <p className="mt-4 mb-4 text-sm">
            Note: Codes displayed below are only those eligible for assignment to one or more of the
            selected departments above. Codes already assigned to all selected departments will not
            be displayed. All selected codes will be assigned to all selected departments.
          </p>
        ) : (
          <p className="mt-4 mb-4 text-sm">
            Note: Codes displayed below are only those eligible for unassignment from one or more of
            the selected departments above. Codes already unassigned to all selected departments
            will not be displayed. All selected codes will be unassigned from all departments.
          </p>
        )}
        <DepartmentsTabs Tabs={Tabs} handleSelectTab={handleSelectTab} selectedTab={selectedTab} />

        {Tabs[selectedTab] === 'Assign' ? (
          <AssignGLCodeTable
            handlePaneClose={handlePaneClose}
            selectedDepartments={selectedRows}
            assignGLCode={assignGLCode}
            setAssignGLCode={setAssignGLCode}
            bulkUpdateGLCode={bulkUpdateGLCode}
            isBulkUpdateGLCodeLoading={isBulkUpdateGLCodeLoading}
          />
        ) : (
          <UnassignGLCodeTable
            handlePaneClose={handlePaneClose}
            selectedDepartments={selectedRows}
            unAssignGLCode={unAssignGLCode}
            setUnAssignGLCode={setUnAssignGLCode}
            bulkUpdateGLCode={bulkUpdateGLCode}
            isBulkUpdateGLCodeLoading={isBulkUpdateGLCodeLoading}
          />
        )}
      </SidePanel>
    </>
  );
};

export default BulkUpdateGLCode;
